import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { Image } from "tsi-common-react/src/components/Image/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { stripHtmlToPlainText } from "tsi-common-react/src/utils/text";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { getStyleVariantClass } from "@thelabnyc/thelabui/src/utils/styles";

import { IHotspotContent } from "../models";

import styles from "./index.module.scss";

type HotspotTipKey = "hotspot_one" | "hotspot_two" | "hotspot_three";

/** Plain excerpt for accordion trigger naming (WCAG — image toggle cannot rely only on decorative alt ""). */
function hotspotTipPlainPreview(html: string, maxChars: number): string {
    if (!html.trim()) {
        return "";
    }
    const stripped = stripHtmlToPlainText(html);

    if (!stripped.length) {
        return "";
    }
    return stripped.length > maxChars
        ? `${stripped.slice(0, maxChars - 1)}\u2026`
        : stripped;
}

function hotspotTriggerAriaLabel(
    tipHtml: string,
    hotspotNumber: number,
): string {
    const preview = hotspotTipPlainPreview(tipHtml, 100);
    if (preview) {
        return t`Additional product detail: ${preview}`;
    }
    return t`Additional product hotspot ${hotspotNumber}`;
}

const getTipIds = (hotspotContent: IHotspotContent): HotspotTipKey[] => {
    return (Object.keys(hotspotContent) as Array<keyof IHotspotContent>).filter(
        (key): key is HotspotTipKey => {
            return (
                key === "hotspot_one" ||
                key === "hotspot_two" ||
                key === "hotspot_three"
            );
        },
    );
};

export const HotspotBlock = ({
    hotspotContent,
}: Immutable<{
    hotspotContent: IHotspotContent;
}>): React.ReactNode => {
    const staticURL = useStaticURL();
    const tipIds = getTipIds(hotspotContent);
    const variantClass =
        getStyleVariantClass(styles, hotspotContent.variant) || "";
    const rootClasses = classNames(styles.root, variantClass);

    return (
        <div
            className={rootClasses}
            data-hotspot-variant={hotspotContent.variant}
        >
            <RichText
                className={styles.header}
                html={hotspotContent.header_content}
            />
            <Accordion.Root
                className={styles.accordion}
                type="single"
                defaultValue={tipIds[1]}
                collapsible={true}
            >
                {tipIds.map((tipId, tipIndex) => (
                    <Accordion.Item
                        key={tipId}
                        value={tipId}
                        className={styles.accordionItem}
                    >
                        <Accordion.Header>
                            <Accordion.Trigger
                                className={styles.button}
                                aria-label={hotspotTriggerAriaLabel(
                                    hotspotContent[tipId] || "",
                                    tipIndex + 1,
                                )}
                            >
                                <div className={styles.pulse} aria-hidden />
                                <Image
                                    lazy={false}
                                    src={`${staticURL}img/pdp/hotspot/plus.png`}
                                    className={styles.buttonImageExpand}
                                    aria-hidden="true"
                                    alt={""}
                                />
                                <Image
                                    lazy={false}
                                    src={`${staticURL}img/pdp/hotspot/minus.png`}
                                    className={styles.buttonImageCollapse}
                                    aria-hidden="true"
                                    alt={""}
                                />
                            </Accordion.Trigger>
                        </Accordion.Header>
                        <Accordion.Content className={styles.accordionPanel}>
                            <RichText html={hotspotContent[tipId] || ""} />
                        </Accordion.Content>
                    </Accordion.Item>
                ))}
            </Accordion.Root>
        </div>
    );
};
